import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, ChevronDown, MapPin, Phone, Zap } from "lucide-react";
import { Logo } from "./Logo";
import { useLang, LANGS } from "../i18n";
import { scrollToId, EASE } from "./Reveal";

const SECTIONS = [
  { id: "synergy", key: "about" },
  { id: "products", key: "products" },
  { id: "specs", key: "specs" },
  { id: "tech", key: "tech" },
  { id: "contact", key: "contact" },
];

const LangSwitch = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  return (
    <div className={"relative " + className} onClick={(e) => e.stopPropagation()}>
      <button
        data-testid="nav-lang-dropdown"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 border border-line px-3 py-2 font-mono2 text-xs uppercase tracking-widest text-txt-2 hover:text-txt hover:border-hadron-cyan/50 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe size={14} className="text-hadron-cyan" />
        {lang}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="absolute right-0 top-full mt-2 w-44 z-50 bg-ink-2 border border-line shadow-2xl shadow-black/50"
            role="listbox"
          >
            {LANGS.map((l) => (
              <li key={l.code}>
                <button
                  data-testid={`nav-lang-option-${l.code}`}
                  onClick={() => { setLang(l.code); setOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 font-mono2 text-xs uppercase tracking-widest transition-colors ${l.code === lang ? "text-hadron-cyan bg-hadron-cyan/10" : "text-txt-2 hover:text-txt hover:bg-white/5"}`}
                >
                  {l.label}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); setTimeout(() => scrollToId(id), open ? 250 : 0); };

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      {/* announcement bar */}
      <div className="bg-hadron-deep border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between gap-4 py-1.5 font-mono2 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-txt-2">
          <span className="hidden md:flex items-center gap-2" data-testid="topbar-address">
            <MapPin size={11} className="text-hadron-cyan" />
            Podolany 88, 32-420 Gdów, Poland
          </span>
          <a href="tel:+48122002588" data-testid="topbar-phone" className="flex items-center gap-2 hover:text-txt transition-colors">
            <Phone size={11} className="text-hadron-cyan" />
            +48 12 2002 588
          </a>
          <span className="hidden sm:flex items-center gap-2 bg-hadron-cyan/10 border border-hadron-cyan/30 text-hadron-cyan px-2.5 py-0.5 rounded-full" data-testid="topbar-badge">
            <Zap size={11} />
            {t.topbar.badge}
          </span>
        </div>
      </div>

      {/* nav */}
      <div className={`transition-all duration-300 ${scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-line" : "bg-transparent"}`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between py-3.5">
          <button data-testid="nav-brand-logo" onClick={() => go("top")} className="shrink-0" aria-label="Hadron Energy — home">
            <Logo />
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                data-testid={`nav-link-${s.id}`}
                onClick={() => go(s.id)}
                className="font-mono2 text-[11px] uppercase tracking-[0.18em] text-txt-2 hover:text-hadron-cyan transition-colors"
              >
                {t.nav[s.key]}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LangSwitch />
            <button
              data-testid="nav-quote-button"
              onClick={() => go("contact")}
              className="hidden sm:inline-flex items-center gap-2 bg-hadron-cyan px-4 py-2.5 font-mono2 text-[11px] uppercase tracking-[0.18em] text-ink font-medium hover:bg-hadron-green transition-colors clip-frame"
            >
              {t.nav.quote}
            </button>
            <button
              data-testid="nav-mobile-toggle"
              className="lg:hidden text-txt p-2"
              onClick={() => setOpen((o) => !o)}
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden fixed inset-0 -z-10 bg-ink/97 backdrop-blur-xl pt-28 px-6"
          >
            <nav className="flex flex-col gap-2">
              {SECTIONS.map((s, i) => (
                <motion.button
                  key={s.id}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4, ease: EASE }}
                  onClick={() => go(s.id)}
                  className="text-left font-display uppercase font-bold text-4xl text-txt hover:text-hadron-cyan transition-colors py-2 border-b border-line"
                >
                  {t.nav[s.key]}
                </motion.button>
              ))}
              <button
                onClick={() => go("contact")}
                className="mt-6 bg-hadron-cyan clip-frame px-6 py-4 font-mono2 text-xs uppercase tracking-[0.2em] text-ink"
              >
                {t.nav.quote}
              </button>
              <p className="mt-6 font-mono2 text-[11px] uppercase tracking-widest text-txt-3">+48 12 2002 588</p>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
